import * as mapboxsearchcore from "./mapbox-search-core/index-esm.004e69e31432.js";
import TomSelect from "./tom-select/tom-select.0c9bda91e62b.js";
import {
  renderTurquoiseSpinner,
  addTomSelectAriaLabel,
} from "./fancy-dropdowns.d2d8f5945e1a.js";
import { FloatingLabel } from "./floating-label.895f0e243516.js";

/**
 * An event dispatched from the location selector when the selection has changed.
 */
export const LocationSelectorChangedEventType = "tqc:location-selector:changed";

const DEFAULT_MAPBOX_SUGGEST_OPTIONS = {
  types: ["place", "postcode", "neighborhood", "district", "locality"],
  country: "us",
  language: "en",
  limit: 3,
};

class LocationSelectorChangedEvent extends CustomEvent {
  constructor(lat, lng, locationName) {
    super(LocationSelectorChangedEventType, {
      bubbles: true,
      detail: {
        lat,
        lng,
        locationName,
      },
    });
  }
}

class LocationSelector extends HTMLElement {
  constructor() {
    super();
    this.parent = this.getRootNode() || document;
  }

  connectedCallback() {
    this.currentLatInput = this.parent.querySelector(
      'input[name="search_lat"]',
    );
    this.currentLngInput = this.parent.querySelector(
      'input[name="search_lng"]',
    );
    this.ratesForm = this.parent.querySelector("#rates-form");

    this.mapboxEnabled = this.hasAttribute("mapbox-enabled");
    this.locationConfig = this.loadLocationConfig();

    this.initialize();
  }

  loadLocationConfig() {
    const configEl = this.parent.getElementById("location-selector-config");
    if (!configEl) {
      return null;
    }
    return JSON.parse(configEl.textContent);
  }

  disconnectedCallback() {
    this.ratesForm.removeEventListener(
      "submit",
      this.zipCodeFormSubmissionHandler,
    );
  }

  initialize() {
    if (this.mapboxEnabled) {
      this.setupMapboxSearch();
    } else {
      this.setupZipCodeInput();
    }
  }

  setupZipCodeInput() {
    this.zipCodeFormSubmissionHandler();

    const input = this.querySelector("input");
    if (input) {
      FloatingLabel.withInput(this, input);
    }
  }

  getDefaultTomSelectOptions(mapboxSearch, sessionToken) {
    return {
      valueField: "name",
      labelField: "name",
      searchField: "name",
      sortField: [{ field: "$order" }, { field: "$score" }], // Disable tom-select sorting
      dropdownParent:
        this.parent.querySelector("#care-location").parentElement.parentElement,
      loadThrottle: 300,
      selectOnTab: true,
      load: async function (query, callback) {
        const results = await mapboxSearch.suggest(query, {
          sessionToken,
          ...DEFAULT_MAPBOX_SUGGEST_OPTIONS,
        });

        const locationOptions = results.suggestions.map((suggestion) => {
          const state_abbr = suggestion.context.region.region_code;
          const place_name = `${suggestion.name}, ${state_abbr}`;
          return {
            mapbox_id: suggestion.mapbox_id,
            name: place_name,
          };
        });
        callback(locationOptions);
      },
      render: {
        loading: renderTurquoiseSpinner,
        item: function (data, escape) {
          return `
              <div data-mapbox-id="${escape(data.mapbox_id)}" translate="no">${escape(
                data.name,
              )}</div>
          `;
        },
        option: function (data, escape) {
          return `<div translate="no">${escape(data.name)}</div>`;
        },
      },
    };
  }

  getConfiguredTomSelectOptions(mapboxSearch, sessionToken) {
    const config = this.locationConfig;
    const { filter_place_name: filterPlaceName, ...mapboxOptions } =
      config.mapbox_suggest_options;

    const mapboxSuggestOptions = {
      ...DEFAULT_MAPBOX_SUGGEST_OPTIONS,
      ...mapboxOptions,
    };

    const defaultTomSelectOptions = this.getDefaultTomSelectOptions(
      mapboxSearch,
      sessionToken,
    );

    const load = async function (query, callback) {
      const results = await mapboxSearch.suggest(query, {
        sessionToken,
        ...mapboxSuggestOptions,
      });
      const data = [];
      results.suggestions.forEach((suggestion) => {
        if (suggestion.context?.place?.name !== filterPlaceName) {
          return;
        }
        const state_abbr = suggestion.context.region.region_code;
        const place_name = `${suggestion.name}, ${state_abbr}`;
        data.push({
          mapbox_id: suggestion.mapbox_id,
          name: place_name,
        });
      });
      callback(data);
    };

    const noResultsMessage = config.no_results_message;

    return {
      ...defaultTomSelectOptions,
      load,
      render: {
        ...defaultTomSelectOptions.render,
        no_results: (_data, _escape) => {
          return `<div class="no-results pv-text-body-sm pv-text-subdued">${noResultsMessage}</div>`;
        },
      },
    };
  }

  zipCodeFormSubmissionHandler() {
    this.ratesForm.addEventListener("submit", (_) => {
      const actionInput = this.parent.querySelector(
        'input[name="action"]',
      ).value;
      // If the a zip code is being submitted from the search bar, clear the geolocation in case it changed
      if (actionInput === "search-bar") {
        this.clearGeoLocation();
      }
    });
  }

  updateGeoLocation(lat, lng, locationName) {
    this.currentLatInput.value = lat;
    this.currentLngInput.value = lng;
    const event = new LocationSelectorChangedEvent(lat, lng, locationName);
    this.dispatchEvent(event);
  }

  clearGeoLocation() {
    this.currentLatInput.value = "";
    this.currentLngInput.value = "";
  }

  setupMapboxSearch() {
    this.mapConfig = JSON.parse(
      this.parent.getElementById("mapbox-config").textContent,
    );
    const { SearchBoxCore, SessionToken } = mapboxsearchcore;

    const mapboxSearch = new SearchBoxCore({
      accessToken: this.mapConfig.mapboxToken,
    });
    const sessionToken = new SessionToken();

    const locationSelectorEl = this.parent.querySelector("#care-location");
    let locationDropdown;
    const hasCustomConfig = this.locationConfig?.mapbox_suggest_options;
    if (hasCustomConfig) {
      locationDropdown = new TomSelect(
        locationSelectorEl,
        this.getConfiguredTomSelectOptions(mapboxSearch, sessionToken),
      );
    } else {
      locationDropdown = new TomSelect(
        locationSelectorEl,
        this.getDefaultTomSelectOptions(mapboxSearch, sessionToken),
      );
    }

    addTomSelectAriaLabel(locationSelectorEl, locationDropdown);

    FloatingLabel.withTomSelect(this, locationDropdown);

    locationDropdown.on("change", async (location) => {
      if (location) {
        const selectedOption = locationDropdown.getItem(location);
        const mapboxId = selectedOption.getAttribute("data-mapbox-id");
        const result = await mapboxSearch.retrieve(
          { mapbox_id: mapboxId },
          { sessionToken },
        );
        if (result?.features?.length > 0) {
          const feature = result.features[0];
          const lat = feature.geometry.coordinates[1];
          const lng = feature.geometry.coordinates[0];

          this.updateGeoLocation(lat, lng, location);

          const event = new LocationSelectorChangedEvent(lat, lng, location);
          selectedOption.dispatchEvent(event);

          // Ensure unselected options from previous searches are cleared
          locationDropdown.clearOptions();
        }
      }
    });

    locationDropdown.on("dropdown_close", () => {
      // We need this for the selector to behave as expected due to how TomSelect is implemented
      locationDropdown.blur();
    });
  }
}

customElements.define("location-selector", LocationSelector);
